/**
 * `/` — entry redirect (SCH Milestone A; retargeted in SCH-AI-2a; guardian
 * routing added in P-SCH-V1 Phase 3, ADR-0103).
 *
 * Pure traffic cop. Spinner while the session + guardian-link + resolved-flag
 * queries resolve, then:
 *  - anonymous → `/login`
 *  - GUARDIAN (≥ 1 linked child) on a coaching centre (`familyPortalEnabled`)
 *    → `/parent` (the read-only Parent Portal — a separate parent login, not
 *    the student-embedded Family view)
 *  - everyone else (students, and guardians when the portal flag is off) →
 *    `/home` (the Slice-2a dashboard, which itself falls back to `/catalog`
 *    when `learnerAnalyticsEnabled` is off).
 *
 * Edge case — a user who is BOTH a student and a guardian: coaching-centre
 * parents are parent-only accounts, so we prefer `/parent` whenever they have
 * linked children AND the portal is on. They can still reach the student app
 * by navigating to `/home` directly if they're also enrolled; we just don't
 * LAND them there. Keeping the rule simple (has children + flag → /parent)
 * avoids guessing enrollment state at the redirect.
 *
 * All hooks run unconditionally and we gate on `.data` — the guardian-children
 * query is the learner-accessible `learning.learner.guardian.children`
 * (returns `{ children: [] }` for a plain student), and the resolved flags
 * come from `useResolvedFeatures()`.
 */
import { createFileRoute, Navigate } from '@tanstack/react-router';
import { trpc } from '../lib/trpc.js';
import { useCurrentUser } from '../lib/auth.js';
import { useResolvedFeatures } from '../lib/features.js';
import { CenteredLoading } from '../components/loading.js';

export const Route = createFileRoute('/')({
  component: IndexRedirect,
});

function IndexRedirect(): React.JSX.Element {
  const me = useCurrentUser();
  const authed = Boolean(me.data);
  // Both queries are disabled while signed out (children gates on a session;
  // resolvedFeatures gates on `me.data` inside the hook), so they never fire
  // for anonymous visitors.
  const childrenQ = trpc.learning.learner.guardian.children.useQuery(undefined, {
    enabled: authed,
  });
  const features = useResolvedFeatures();

  if (me.isPending) return <CenteredLoading />;
  if (!authed) return <Navigate to="/login" replace />;

  // Wait for the two routing inputs before deciding, so we never bounce a
  // guardian to /home and then re-route them. If a query errors we degrade to
  // the student dashboard (the safe default that everyone can reach).
  if (childrenQ.isPending || features.isPending) return <CenteredLoading />;

  const isGuardian = (childrenQ.data?.children.length ?? 0) > 0;
  const portalOn = features.data?.familyPortalEnabled === true;
  const analyticsOn = features.data?.learnerAnalyticsEnabled === true;

  if (isGuardian && portalOn) return <Navigate to="/parent" replace />;
  // Coaching-centre students (learner analytics off) have no dashboard — land
  // them straight on their course catalog instead of bouncing through /home
  // (which would only redirect here anyway).
  if (!analyticsOn) return <Navigate to="/catalog" replace />;
  return <Navigate to="/home" replace />;
}
